import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Link } from 'react-router-dom';
import { Heart, Briefcase, PartyPopper, Quote } from 'lucide-react';
// Importamos las imágenes locales para usarlas como fallback
import defaultHeroImage from '../assets/images/hero-banquetes.jpg';
import defaultChefImage from '../assets/images/chef-nosotros.jpg';

const Inicio = () => {
  // Estados para las imágenes dinámicas, inicializados como null para evitar el parpadeo
  const [heroImageUrl, setHeroImageUrl] = useState(null);
  const [nosotrosImageUrl, setNosotrosImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Estado para los testimonios
  const [testimonials, setTestimonials] = useState([]);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      // 1. Cargar las imágenes dinámicas desde la tabla site_settings
      const { data: settings, error: settingsError } = await supabase
        .from('site_settings')
        .select('key, value')
        .in('key', ['hero_image_url', 'nosotros_image_url']);

      if (settingsError) {
        console.error('Error fetching site settings:', settingsError);
        // Si hay un error, usamos las imágenes por defecto
        setHeroImageUrl(defaultHeroImage);
        setNosotrosImageUrl(defaultChefImage);
      } else {
        const heroImg = settings.find(s => s.key === 'hero_image_url');
        const nosotrosImg = settings.find(s => s.key === 'nosotros_image_url');
        
        setHeroImageUrl(heroImg?.value || defaultHeroImage);
        setNosotrosImageUrl(nosotrosImg?.value || defaultChefImage);
      }
      
      // 2. Cargar los testimonios desde la tabla testimonials
      const { data: testimonialsData, error: testimonialsError } = await supabase
        .from('testimonials')
        .select('*');

      if (testimonialsError) {
        console.error('Error fetching testimonials:', testimonialsError);
      } else {
        setTestimonials(testimonialsData || []);
      }
      
      setLoading(false);
    };

    fetchData();
  }, []);

  const services = [
    {
      icon: <Heart className="h-12 w-12 text-orange-500" />,
      title: 'Bodas y Celebraciones',
      description: 'Hacemos del día más importante de tu vida una experiencia culinaria mágica e inolvidable.',
    },
    {
      icon: <Briefcase className="h-12 w-12 text-orange-500" />,
      title: 'Eventos Corporativos',
      description: 'Impresiona a tus clientes y colaboradores con un servicio profesional y platillos de alta cocina.',
    },
    {
      icon: <PartyPopper className="h-12 w-12 text-orange-500" />,
      title: 'Fiestas Privadas',
      description: 'Desde cumpleaños hasta reuniones familiares, llevamos el mejor sabor a cualquier tipo de celebración.',
    },
  ];

  return (
    <div>
      {/* Hero Section con esqueleto de carga para evitar parpadeo */}
      <section className="relative h-96">
        {loading || !heroImageUrl ? (
          <div className="w-full h-full bg-gray-300 animate-pulse"></div>
        ) : (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImageUrl})` }}
          >
            <div className="absolute inset-0 bg-black opacity-50"></div>
          </div>
        )}
        <div className="relative z-10 h-full flex items-center justify-center text-center text-white px-4">
          <div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">Elegancia y Sabor para tus Eventos</h1>
            <p className="text-lg md:text-xl mb-8">Creamos experiencias culinarias inolvidables para tus momentos más especiales.</p>
            <Link to="/contacto" className="bg-orange-500 text-white hover:bg-orange-600 font-bold py-3 px-8 rounded-lg text-lg transition duration-300">Cotizar Evento</Link>
          </div>
        </div>
      </section>

      {/* Sección de Servicios */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">Nuestros Servicios</h2>
            <p className="mt-4 text-lg text-gray-500">Nos especializamos en una variedad de eventos para adaptarnos a tus necesidades.</p>
          </div>
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {services.map((service) => (
              <div key={service.title} className="text-center p-8 border border-gray-200 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center justify-center h-16 w-16 mx-auto bg-orange-100 rounded-full">{service.icon}</div>
                <h3 className="mt-6 text-xl font-bold text-gray-900">{service.title}</h3>
                <p className="mt-2 text-base text-gray-500">{service.description}</p>
                <Link to="/catalogo" className="mt-4 inline-block text-orange-500 hover:text-orange-700 font-semibold">Ver más &rarr;</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sección "Nuestra Historia" con esqueleto de carga */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              {loading || !nosotrosImageUrl ? (
                <div className="w-full aspect-square bg-gray-300 animate-pulse rounded-lg"></div>
              ) : (
                <img src={nosotrosImageUrl} alt="El Chef" className="rounded-lg shadow-2xl w-full h-auto object-cover aspect-square"/>
              )}
            </div>
            <div className="text-left">
              <p className="text-orange-500 font-semibold">Nuestra Historia</p>
              <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">Pasión por la Alta Cocina</h2>
              <p className="mt-4 text-lg text-gray-600">Con más de 20 años de experiencia, nuestro chef ha dedicado su vida a perfeccionar el arte de la gastronomía. Cada platillo es una obra maestra, preparada con ingredientes frescos y locales para garantizar una calidad insuperable.</p>
              <Link to="/nosotros" className="mt-6 inline-block bg-gray-800 text-white hover:bg-gray-700 font-bold py-3 px-8 rounded-lg transition duration-300">Conoce más</Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Sección de Testimonios */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Lo que dicen nuestros clientes
            </h2>
          </div>
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {loading ? (
              <p className="text-center col-span-3">Cargando testimonios...</p>
            ) : testimonials.length === 0 ? (
              <p className="text-center col-span-3 text-gray-500">No hay testimonios disponibles en este momento.</p>
            ) : (
              testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-gray-50 p-8 rounded-lg shadow-md">
                  <Quote className="h-8 w-8 text-orange-400" />
                  <p className="mt-4 text-base text-gray-600 italic">"{testimonial.quote}"</p>
                  <div className="mt-4">
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.event}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Inicio;